import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import toast from '../../../Components/Toast/Toast';
import { usePrepareAsnGeneration } from './usePrepareAsnGeneration';
import { useGetAsnByPoNumber } from './useGetAsnByPoNumber';

/**
 * Navigates to the ASN page for a PO. If ASN data already exists, navigates
 * immediately; otherwise triggers ASN generation first, then navigates or
 * shows an error toast.
 *
 * @param userId - User identifier.
 * @param poNumber - Purchase order number.
 * @param enabled - Passed to the underlying ASN fetch; controls whether the
 *   query runs (e.g. only when the user can open ASN).
 * @returns `navigateToAsn` callback, combined loading flags, and current ASN
 *   data from the prefetch query.
 */
export const useNavigateToAsn = (
  userId: string,
  poNumber: string,
  enabled: boolean,
) => {
  const history = useHistory();
  const asnRoute = `/users/${userId}/purchase-orders/${poNumber}/asn`;
  const asnQuery = useGetAsnByPoNumber(userId, poNumber, enabled);
  const prepareAsnGeneration = usePrepareAsnGeneration(userId, poNumber);

  const navigateToAsn = useCallback(() => {
    if (asnQuery.data) {
      history.push(asnRoute);
    } else {
      prepareAsnGeneration.mutate(undefined, {
        onSuccess: () => history.push(asnRoute),
        onError: () => {
          toast(
            {
              title: 'Error',
              text: 'An error occurred while generating ASN. Please try again later. If the problem persists, please contact support.',
            },
            { type: 'error', autoClose: 1000 },
          );
        },
      });
    }
  }, [asnQuery.data, asnRoute, prepareAsnGeneration.mutate, history]);

  return {
    navigateToAsn,
    isLoading: asnQuery.isLoading || prepareAsnGeneration.isLoading,
    isGenerating: prepareAsnGeneration.isLoading,
    asnData: asnQuery.data,
  };
};
